import { DeviceNetworkType } from "@tether-web-portal/types/DeviceNetworkType";
import { DeviceListObject } from "@tether-web-portal/api/properties/hooks";
import { DeviceWithLastSample } from "@tether-web-portal/types/DeviceWithLastSample";
import { CovidCo2RiskLevel, getCovidRiskLevel } from "@tether-web-portal/utils/covid-care";
import { isSampleReallyOld, isSampleRecent } from "@tether-web-portal/utils/isSampleRecent";
import Co2AreasIcon from "../areas-icons/Co2AreasIcon";
import HumidityAreaIcon from "../areas-icons/HumidityAreaIcon";
import TempAreasIcon from "../areas-icons/TempAreasIcon";
import LightIcon from "../areas-icons/LightIcon";
import PressureIcon from "../areas-icons/PressureIcon";
import SoundIcon from "../areas-icons/SoundIcon";
import AirwitzPMIcon from "../areas-icons/AirwitzPMIcon";
import WaterIcon from "@tether-web-portal/components/areas/areas-icons/WaterIcon";

type IconComponent = ({ size, color }: { size: number; color?: string | undefined }) => JSX.Element;
export interface ReadingValueBreakdown {
  value: number;
  label: string;
  title: string;
  subTitle?: string;
  IconComponent: IconComponent;
  isHealthy: boolean;
  ignoreHealthReading?: boolean;
}

type HealthMetricReadingField = "temp" | "co2" | "humidity";

const healthFields: HealthMetricReadingField[] = ["co2", "humidity", "temp"];

export enum SampleReadingBreakdownField {
  temp = "temp",
  co2 = "co2",
  humidity = "humidity",
  pressure = "pressure",
  light = "light",
  sound = "sound",
  occupied = "occupied",
  pm1 = "pm1",
  pm2_5 = "pm2_5",
  pm10 = "pm10",
  tvoc = "tvoc",
  maxTVOC = "maxTVOC",
  minTVOC = "minTVOC",
  flowRate = "flowRate",
  waterUsage = "waterUsage",
  temperatureInlet = "temperatureInlet",
  temperatureOutlet = "temperatureOutlet",
}

export interface SampleBreakdown extends Partial<Record<SampleReadingBreakdownField, ReadingValueBreakdown>> {
  healthyBreakdownText?: string;
  signalStrengthPercentage?: number;
  battery?: number;
  isOnline: boolean;
  isReallyOld: boolean;
  covidCare?: {
    riskLevel: CovidCo2RiskLevel;
    riskLevelLabel: string;
    riskLevelColor: string;
  };
}

type Formatter = (value: number) => string;
type HealthyChecker = (value: number) => boolean;

const IntlFormatter = new Intl.NumberFormat(navigator.language, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const isTempHealthy: HealthyChecker = (temp: number) => temp >= 18 && temp <= 24;
const isHumidityHealthy: HealthyChecker = (humidity: number) => humidity! < 60;
const isC02Healthy: HealthyChecker = (co2Value: number) =>
  getCovidRiskLevel(co2Value) !== CovidCo2RiskLevel.HIGH;

const readingSampleMetadata: Record<
  SampleReadingBreakdownField,
  {
    unit: string;
    IconComponent: IconComponent;
    title: string;
    subTitle?: string;
    ignoreHealthReading?: boolean;
    format?: Formatter;
    isHealthy?: HealthyChecker;
  }
> = {
  temp: {
    unit: "°C",
    IconComponent: TempAreasIcon,
    title: "Temp",
    isHealthy: isTempHealthy,
  },
  temperatureInlet: {
    unit: "°C",
    IconComponent: TempAreasIcon,
    title: "Temp",
    subTitle: "inlet",
    ignoreHealthReading: true,
  },
  temperatureOutlet: {
    unit: "°C",
    IconComponent: TempAreasIcon,
    title: "Temp",
    subTitle: "outlet",
    ignoreHealthReading: true,
  },
  humidity: {
    unit: "%",
    IconComponent: HumidityAreaIcon,
    title: "Humidity",
    isHealthy: isHumidityHealthy,
    ignoreHealthReading: false,
  },
  flowRate: {
    unit: "m3/h",
    IconComponent: WaterIcon,
    title: "Water Flow",
    ignoreHealthReading: true,
  },
  waterUsage: {
    unit: "L",
    IconComponent: WaterIcon,
    title: "Water Usage",
    ignoreHealthReading: true,
  },
  pressure: {
    unit: "Pa",
    IconComponent: PressureIcon,
    title: "Pressure",
    ignoreHealthReading: true,
  },
  light: {
    unit: "Lux",
    IconComponent: LightIcon,
    title: "Light",
    ignoreHealthReading: true,
  },
  sound: {
    unit: "dB",
    IconComponent: SoundIcon,
    title: "Sound",
  },
  pm1: {
    unit: "µg/m3",
    IconComponent: AirwitzPMIcon,
    title: "PM 1",
    ignoreHealthReading: true,
  },
  pm2_5: {
    unit: "µg/m3",
    IconComponent: AirwitzPMIcon,
    title: "PM 2.5",
    ignoreHealthReading: true,
  },
  pm10: {
    unit: "µg/m3",
    IconComponent: AirwitzPMIcon,
    title: "PM 10",
    ignoreHealthReading: true,
  },
  tvoc: {
    unit: "ppb",
    IconComponent: AirwitzPMIcon,
    title: "TVOC",
    ignoreHealthReading: true,
  },
  minTVOC: {
    unit: "ppb",
    IconComponent: AirwitzPMIcon,
    title: "Min TVOC",
    ignoreHealthReading: true,
  },
  maxTVOC: {
    unit: "ppb",
    IconComponent: AirwitzPMIcon,
    title: "Max TVOC",
    ignoreHealthReading: true,
  },
  co2: {
    unit: "ppm",
    IconComponent: Co2AreasIcon,
    title: "CO2",
    isHealthy: isC02Healthy,
    ignoreHealthReading: false,
  },
  occupied: {
    IconComponent: AirwitzPMIcon,
    unit: "",
    title: "Occupancy",
    format: (occupied) => (occupied === 1 ? "Occupied" : "Unoccupied"),
    isHealthy: (occupied) => (occupied === 1 ? false : true),
    ignoreHealthReading: true,
  },
};

const genBreakdown = (
  value: number,
  key: SampleReadingBreakdownField,
  override?: Partial<ReadingValueBreakdown>,
): ReadingValueBreakdown | undefined => {
  if (value === undefined) return undefined;
  const metadata = readingSampleMetadata[key];
  const label = IntlFormatter.format(value);
  return {
    value: value,
    label: `${label} ${metadata.unit}`,
    isHealthy: metadata.isHealthy?.(value) ?? true,
    ignoreHealthReading: metadata.ignoreHealthReading,
    title: metadata.title,
    subTitle: metadata.subTitle,
    IconComponent: metadata.IconComponent,
    ...override,
  };
};

export const makeSampleBreakdown = (
  device: DeviceWithLastSample | DeviceListObject | undefined,
): SampleBreakdown | undefined => {
  const latestSample = device?.lastSample;

  if (!device || !latestSample) {
    return undefined;
  }

  const { sample } = latestSample || {};

  const isOnline = isSampleRecent(latestSample, device.type);
  const isReallyOld = isSampleReallyOld(latestSample);

  const breakdown: SampleBreakdown = {
    isOnline,
    isReallyOld,
    temp: genBreakdown(sample?.temperature, SampleReadingBreakdownField.temp),
    temperatureInlet: genBreakdown(sample?.temperatureInlet, SampleReadingBreakdownField.temperatureInlet),
    temperatureOutlet: genBreakdown(sample?.temperatureOutlet, SampleReadingBreakdownField.temperatureOutlet),
    humidity: genBreakdown(sample?.humidity, SampleReadingBreakdownField.humidity),
    flowRate: genBreakdown(sample?.flowRate, SampleReadingBreakdownField.flowRate),
    waterUsage: genBreakdown(sample?.waterUsage, SampleReadingBreakdownField.waterUsage),
    pressure: genBreakdown(sample?.pressure, SampleReadingBreakdownField.pressure),
    light: genBreakdown(sample?.light, SampleReadingBreakdownField.light),
    sound: genBreakdown(sample?.sound, SampleReadingBreakdownField.sound),
    pm1: genBreakdown(sample?.pm1, SampleReadingBreakdownField.pm1),
    pm2_5: genBreakdown(sample?.pm2_5, SampleReadingBreakdownField.pm2_5),
    pm10: genBreakdown(sample?.pm10, SampleReadingBreakdownField.pm10),
    tvoc: genBreakdown(sample?.tvoc, SampleReadingBreakdownField.tvoc),
    minTVOC: genBreakdown(sample?.minTVOC, SampleReadingBreakdownField.minTVOC),
    maxTVOC: genBreakdown(sample?.maxTVOC, SampleReadingBreakdownField.maxTVOC),
    ...(sample?.occupied !== undefined && {
      occupied: genBreakdown(sample?.occupied ? 1 : 0, SampleReadingBreakdownField.occupied),
    }),
    co2: genBreakdown(sample?.co2, SampleReadingBreakdownField.co2),
  };

  breakdown.battery = sample?.battery ? latestSample.sample.battery / 100 : undefined;

  breakdown.signalStrengthPercentage =
    device.network === DeviceNetworkType.MQTT ? 1 : device.signalStrengthPercentage?.percentage;

  const fieldsWithValue = healthFields.filter((f) => breakdown[f] && !breakdown[f]!.ignoreHealthReading);
  const fieldsWithHealthyValue = fieldsWithValue.filter((f) => breakdown[f]?.isHealthy);

  const healthyBreakdownText = `${fieldsWithHealthyValue.length}/${fieldsWithValue.length} health metrics are in healthy range`;
  breakdown.healthyBreakdownText = fieldsWithValue.length === 0 ? "" : healthyBreakdownText;

  return breakdown;
};
